import { PropertyStatusEnum, type UserProperty } from '@mr-website/schemas';
import { MapPin } from 'lucide-react';
import * as React from 'react';

import { api } from '@/api/client';
import { BookmarkSavedDate } from '@/components/blocks/BookmarkSavedDate';
import type { DataBadgeVariant } from '@/components/blocks/DataCard';
import { DataCardBadge } from '@/components/blocks/DataCard';
import { DataCard } from '@/components/blocks/DataCard';
import { DataCardContent } from '@/components/blocks/DataCard';

function propertyStatusBadgeVariant(status: string): DataBadgeVariant {
  switch (status) {
    case PropertyStatusEnum.AVAILABLE:
      return 'emerald';
    case PropertyStatusEnum.PENDING:
      return 'orange';
    case PropertyStatusEnum.CONTRACTED:
      return 'blue';
    case PropertyStatusEnum.SOLD:
      return 'slate';
    case PropertyStatusEnum.DRAFT:
      return 'cyan';
    case PropertyStatusEnum.REMOVED:
    case PropertyStatusEnum.DELETED:
      return 'rose';
    default:
      return 'default';
  }
}

export interface PropertyCardProps {
  property: UserProperty;
  className?: string;
  onDeleted?: () => void;
  bookmarkIconVariant?: 'star' | 'prohibit';
  showBookmark?: boolean;
}

export function PropertyCard({
  property,
  className,
  onDeleted,
  bookmarkIconVariant,
  showBookmark = true,
}: PropertyCardProps) {
  const [isDeleting, setIsDeleting] = React.useState(false);

  const handleRemove = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      await api.delete(`/user/properties/${property.id}`);
      onDeleted?.();
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <DataCard
      imageSrc={property.photo}
      imageAlt={`${property.street ?? ''}, ${property.city ?? ''}`}
      imageFallbackSrc="/static/images/nophoto-large.jpg"
      href={`/property/${property.id}`}
      badgeText={property.propertyStatus}
      badgeVariant={propertyStatusBadgeVariant(property.propertyStatus ?? '')}
      showBookmarkIcon={showBookmark}
      isBookMarked={showBookmark}
      bookmarkIconVariant={bookmarkIconVariant}
      onBookmarkClick={showBookmark ? handleRemove : undefined}
      bookmarkLabel={showBookmark ? 'Remove from list' : undefined}
      className={className}
    >
      <DataCardContent>
        <span className="text-data-card-xl font-bold">
          {property.purchasePrice ? `$${property.purchasePrice.toLocaleString()}` : 'Price N/A'}
        </span>

        {/* Address */}
        <div>
          <div className="flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5 shrink-0 text-text-muted" />
            <span
              className="truncate text-data-card-md font-semibold text-text-address"
              title={property.street}
            >
              {property.street}
            </span>
          </div>
          <span className="pl-5 text-data-card-subtitle text-data-card-xs tracking-wide">
            {property.city}, {property.state}
          </span>
        </div>

        <div className="flex min-h-5 flex-wrap items-center gap-1.5">
          {property.propertyClass && (
            <DataCardBadge>{property.propertyClass.toUpperCase()} Class</DataCardBadge>
          )}
          {property.propertyType && <DataCardBadge>{property.propertyType}</DataCardBadge>}
        </div>

        {showBookmark && <BookmarkSavedDate date={property.date} />}
      </DataCardContent>
    </DataCard>
  );
}
